import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { createDeleteStowagePlanChange, createStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { isManualCargo, isManualCargoOperation } from '../cargoOperationUtils';
import { findMergeableCargo } from '../stowagePlanComparison/itemFindersUtils';

/**
 * Check if a cargo can be merged with another cargo.
 * A cargo can be merged if there exists another cargo with:
 * - Different data source (one manual/OrcaWeb, one from IMOS)
 * - Same product ID
 * - Same cargo number
 * - Same voyage number
 *
 * @param stowagePlan - The current stowage plan
 * @param cargo - The cargo to check
 * @returns true if a mergeable cargo exists
 */
export const canMergeCargo = (stowagePlan: StowagePlan, cargo: RotationCargo): boolean => {
    if (!stowagePlan || !cargo) return false;

    const mergeCandidate = findMergeableCargo(cargo, stowagePlan.cargoes);
    return mergeCandidate !== null;
};

/**
 * Get the cargo that can be merged with the given cargo.
 *
 * @param stowagePlan - The current stowage plan
 * @param cargo - The cargo to find a merge candidate for
 * @returns The mergeable cargo, or null if none found
 */
export const getMergeableCargo = (stowagePlan: StowagePlan, cargo: RotationCargo): RotationCargo | null => {
    if (!stowagePlan || !cargo) return null;

    return findMergeableCargo(cargo, stowagePlan.cargoes);
};

/**
 * Merge a manual cargo with an IMOS cargo.
 * The IMOS cargo is kept (as it has the official IMOS ID),
 * and the manual cargo is deleted. Any cargo operations associated
 * with the manual cargo are matched with corresponding IMOS cargo operations
 * (by berth call, port call, and operation type), and stowage items are transferred.
 * Unmatched manual cargo operations are deleted.
 *
 * @param stowagePlan - The current stowage plan (will be mutated)
 * @param cargo - The cargo initiating the merge (can be either manual or IMOS)
 * @returns Array of stowage plan changes to persist the merge
 */
export const mergeCargoes = (stowagePlan: StowagePlan, cargo: RotationCargo): StowagePlanChange[] => {
    if (!stowagePlan || !cargo) return [];

    const mergeCandidate = findMergeableCargo(cargo, stowagePlan.cargoes);
    if (!mergeCandidate) return [];

    // Determine which cargo is manual and which is from IMOS
    const isCurrentManual = isManualCargo(cargo);
    const manualCargo = isCurrentManual ? cargo : mergeCandidate;
    const imosCargo = isCurrentManual ? mergeCandidate : cargo;

    const changes: StowagePlanChange[] = [];

    // Find all cargo operations for both cargoes
    const manualCargoOperations = stowagePlan.cargoOperations.filter((op) => op.cargoGuid === manualCargo.cargoGuid);
    const imosCargoOperations = stowagePlan.cargoOperations.filter((op) => op.cargoGuid === imosCargo.cargoGuid);

    // For each manual cargo operation, find matching IMOS cargo operation and merge
    for (const manualOp of manualCargoOperations) {
        // Find matching IMOS cargo operation (same berth call, port call, operation type)
        const matchingImosOp = findMatchingCargoOperation(manualOp, imosCargoOperations);

        if (matchingImosOp) {
            // Transfer stowage items from manual operation to IMOS operation
            const stowageItemsToTransfer = stowagePlan.stowageItems.filter((item) => item.cargoOperationGuid === manualOp.cargoOperationGuid);

            for (const stowageItem of stowageItemsToTransfer) {
                const updateData = {
                    cargoOperationGuid: matchingImosOp.cargoOperationGuid,
                    extraData: {
                        descriptions: [`Merged stowage from manual cargo operation to IMOS cargo operation`],
                    },
                };

                changes.push(
                    createStowagePlanChange(stowagePlan.stowagePlanGuid, stowageItem.stowageGuid, StowageChangeDataType.StowageItem, updateData, true)
                );

                // Update the local stowage item reference
                stowageItem.cargoOperationGuid = matchingImosOp.cargoOperationGuid;
                stowageItem.cargoOperation = matchingImosOp;
            }
        }

        // Delete the manual cargo operation (whether matched or not)
        changes.push(
            createDeleteStowagePlanChange(stowagePlan.stowagePlanGuid, manualOp.cargoOperationGuid, StowageChangeDataType.RotationCargoOperation)
        );

        // Remove from stowage plan
        stowagePlan.cargoOperations = stowagePlan.cargoOperations.filter((op) => op.cargoOperationGuid !== manualOp.cargoOperationGuid);

        // Also remove from berth call if present
        if (manualOp.berthCall?.cargoOperations) {
            manualOp.berthCall.cargoOperations = manualOp.berthCall.cargoOperations.filter(
                (op) => op.cargoOperationGuid !== manualOp.cargoOperationGuid
            );
        }
    }

    // Delete the manual cargo
    changes.push(createDeleteStowagePlanChange(stowagePlan.stowagePlanGuid, manualCargo.cargoGuid, StowageChangeDataType.RotationCargo));

    // Remove the manual cargo from the stowage plan
    stowagePlan.cargoes = stowagePlan.cargoes.filter((c) => c.cargoGuid !== manualCargo.cargoGuid);

    return changes;
};

/**
 * Find a matching cargo operation based on berth call, port call, and operation type.
 * Used when merging cargoes to find corresponding IMOS operations for manual operations.
 */
const findMatchingCargoOperation = (manualOp: RotationCargoOperation, imosOperations: RotationCargoOperation[]): RotationCargoOperation | null => {
    return (
        imosOperations.find(
            (imosOp) =>
                imosOp.berthCallGuid === manualOp.berthCallGuid &&
                imosOp.portCallGuid === manualOp.portCallGuid &&
                imosOp.operationType === manualOp.operationType
        ) ?? null
    );
};

/**
 * Get a description of the merge operation for display in the UI.
 *
 * @param cargo - The source cargo
 * @param mergeCandidate - The target cargo to merge with
 * @returns A human-readable description of the merge
 */
export const getCargoMergeDescription = (cargo: RotationCargo, mergeCandidate: RotationCargo): string => {
    const isCurrentManual = isManualCargo(cargo);
    const manualCargo = isCurrentManual ? cargo : mergeCandidate;
    const imosCargo = isCurrentManual ? mergeCandidate : cargo;

    return `Merge manual cargo ${manualCargo.voyageNumber}-${String(manualCargo.cargoNumber).padStart(3, '0')} with IMOS cargo ${
        imosCargo.voyageNumber
    }-${String(imosCargo.cargoNumber).padStart(3, '0')}`;
};
